import { z } from 'zod';

/** Nation of residence (R-4.2). IHT rules are UK-wide; care/CHC rules vary by nation. */
export const NATIONS = ['england', 'scotland', 'wales', 'northern_ireland'] as const;
export const Nation = z.enum(NATIONS);
export type Nation = z.infer<typeof Nation>;
export const NATION_LABELS: Record<Nation, string> = {
  england: 'England',
  scotland: 'Scotland',
  wales: 'Wales',
  northern_ireland: 'Northern Ireland',
};

/** Life stage of the Principal (spec §4.1). */
export const STAGES = ['gifting', 'care', 'estate'] as const;
export const Stage = z.enum(STAGES);
export type Stage = z.infer<typeof Stage>;
export const STAGE_LABELS: Record<Stage, string> = {
  gifting: 'Gifting',
  care: 'Care',
  estate: 'Estate',
};

/** Workspace roles (spec §3.2). */
export const ROLES = ['owner', 'attorney', 'family', 'principal', 'professional'] as const;
export const Role = z.enum(ROLES);
export type Role = z.infer<typeof Role>;
export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  attorney: 'Attorney',
  family: 'Family member',
  principal: 'Principal',
  professional: 'Professional',
};

/** Per-module visibility keys (R-3.3). */
export const VISIBILITY_MODULES = ['gifts', 'money', 'care', 'health', 'attorney', 'documents'] as const;
export const VisibilityModule = z.enum(VISIBILITY_MODULES);
export type VisibilityModule = z.infer<typeof VisibilityModule>;
export const VISIBILITY_MODULE_LABELS: Record<VisibilityModule, string> = {
  gifts: 'Gifts',
  money: 'Money (accounts and transactions)',
  care: 'Care finances',
  health: 'Health evidence',
  attorney: 'Attorney records',
  documents: 'Documents',
};

export const ModuleVisibility = z.object({
  gifts: z.boolean(),
  money: z.boolean(),
  care: z.boolean(),
  health: z.boolean(),
  attorney: z.boolean(),
  documents: z.boolean(),
});
export type ModuleVisibility = z.infer<typeof ModuleVisibility>;
export const ALL_MODULES_VISIBLE: ModuleVisibility = {
  gifts: true,
  money: true,
  care: true,
  health: true,
  attorney: true,
  documents: true,
};

/** Code modules / vertical slices (ARCHITECTURE §6). Used for audit `module`, deadlines and routing. */
export const APP_MODULES = [
  'workspace',
  'documents',
  'money',
  'gifts',
  'care',
  'health',
  'attorney',
  'platform',
] as const;
export const AppModule = z.enum(APP_MODULES);
export type AppModule = z.infer<typeof AppModule>;

/** Permission actions used by `can(module, action)`. */
export const PERMISSION_ACTIONS = ['view', 'edit', 'export'] as const;
export const PermissionAction = z.enum(PERMISSION_ACTIONS);
export type PermissionAction = z.infer<typeof PermissionAction>;

/** Subscription status (FR-13.x). */
export const SUBSCRIPTION_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'read_only',
  'archive',
  'cancelled',
] as const;
export const SubscriptionStatus = z.enum(SUBSCRIPTION_STATUSES);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatus>;

/** Document categories (FR-2.2). */
export const DOCUMENT_CATEGORIES = [
  'lpa_deputyship',
  'will',
  'bank_statement',
  'pension_statement',
  'care_home_contract',
  'care_invoice',
  'council_letter',
  'nhs_letter',
  'chc_checklist_dst',
  'assessment_report',
  'benefit_letter',
  'gift_evidence',
  'property',
  'insurance',
  'funeral_plan',
  'other',
] as const;
export const DocumentCategory = z.enum(DOCUMENT_CATEGORIES);
export type DocumentCategory = z.infer<typeof DocumentCategory>;
export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  lpa_deputyship: 'LPA / deputyship',
  will: 'Will',
  bank_statement: 'Bank statement',
  pension_statement: 'Pension statement',
  care_home_contract: 'Care home contract',
  care_invoice: 'Care invoice',
  council_letter: 'Council letter',
  nhs_letter: 'NHS letter',
  chc_checklist_dst: 'CHC checklist / DST',
  assessment_report: 'Assessment report',
  benefit_letter: 'Benefit letter',
  gift_evidence: 'Gift evidence',
  property: 'Property',
  insurance: 'Insurance',
  funeral_plan: 'Funeral plan',
  other: 'Other',
};

/** Transaction categories (FR-3.3). `subCategory` is only used for income and living expenses. */
export const TRANSACTION_CATEGORIES = [
  'income',
  'living_expenses',
  'care_fees',
  'care_related',
  'gift',
  'transfer',
  'professional_fees',
  'other',
] as const;
export const TransactionCategory = z.enum(TRANSACTION_CATEGORIES);
export type TransactionCategory = z.infer<typeof TransactionCategory>;
export const TRANSACTION_CATEGORY_LABELS: Record<TransactionCategory, string> = {
  income: 'Income',
  living_expenses: 'Living expenses',
  care_fees: 'Care fees',
  care_related: 'Care-related costs',
  gift: 'Gift',
  transfer: 'Transfer between own accounts',
  professional_fees: 'Professional fees',
  other: 'Other',
};

export const INCOME_SUBCATEGORIES = [
  'state_pension',
  'private_pension',
  'annuity',
  'interest',
  'dividends',
  'rent',
  'benefits',
  'other',
] as const;
export const IncomeSubCategory = z.enum(INCOME_SUBCATEGORIES);
export type IncomeSubCategory = z.infer<typeof IncomeSubCategory>;
export const INCOME_SUBCATEGORY_LABELS: Record<IncomeSubCategory, string> = {
  state_pension: 'State pension',
  private_pension: 'Private pension',
  annuity: 'Annuity',
  interest: 'Interest',
  dividends: 'Dividends',
  rent: 'Rent',
  benefits: 'Benefits',
  other: 'Other income',
};

export const LIVING_EXPENSE_SUBCATEGORIES = [
  'household',
  'food',
  'utilities',
  'insurance',
  'transport',
  'health',
  'personal',
  'other',
] as const;
export const LivingExpenseSubCategory = z.enum(LIVING_EXPENSE_SUBCATEGORIES);
export type LivingExpenseSubCategory = z.infer<typeof LivingExpenseSubCategory>;
export const LIVING_EXPENSE_SUBCATEGORY_LABELS: Record<LivingExpenseSubCategory, string> = {
  household: 'Household',
  food: 'Food',
  utilities: 'Utilities',
  insurance: 'Insurance',
  transport: 'Transport',
  health: 'Health',
  personal: 'Personal',
  other: 'Other living expenses',
};

/** CHC care domains (FR-6.1, National Framework). */
export const CHC_DOMAINS = [
  'breathing',
  'nutrition',
  'continence',
  'skin',
  'mobility',
  'communication',
  'psychological',
  'cognition',
  'behaviour',
  'drug_therapies',
  'altered_consciousness',
  'other_needs',
] as const;
export const ChcDomain = z.enum(CHC_DOMAINS);
export type ChcDomain = z.infer<typeof ChcDomain>;
export const CHC_DOMAIN_LABELS: Record<ChcDomain, string> = {
  breathing: 'Breathing',
  nutrition: 'Nutrition (food and drink)',
  continence: 'Continence',
  skin: 'Skin (including tissue viability)',
  mobility: 'Mobility',
  communication: 'Communication',
  psychological: 'Psychological and emotional needs',
  cognition: 'Cognition',
  behaviour: 'Behaviour',
  drug_therapies: 'Drug therapies and medication',
  altered_consciousness: 'Altered states of consciousness',
  other_needs: 'Other significant care needs',
};

/** Account types (FR-3.1). */
export const ACCOUNT_TYPES = [
  'bank',
  'savings',
  'isa',
  'investments',
  'pension',
  'property',
  'other_asset',
  'debt',
] as const;
export const AccountType = z.enum(ACCOUNT_TYPES);
export type AccountType = z.infer<typeof AccountType>;

export const MARITAL_STATUSES = [
  'single',
  'married',
  'civil_partnership',
  'widowed',
  'divorced',
  'separated',
  'cohabiting',
] as const;
export const MaritalStatus = z.enum(MARITAL_STATUSES);
export type MaritalStatus = z.infer<typeof MaritalStatus>;

export const HOME_OWNERSHIP = [
  'owns_outright',
  'owns_with_mortgage',
  'joint_owner',
  'rents',
  'lives_with_family',
  'other',
] as const;
export const HomeOwnership = z.enum(HOME_OWNERSHIP);
export type HomeOwnership = z.infer<typeof HomeOwnership>;

export const CARE_SETTINGS = [
  'own_home',
  'home_with_care',
  'residential_home',
  'nursing_home',
  'hospital',
  'other',
] as const;
export const CareSetting = z.enum(CARE_SETTINGS);
export type CareSetting = z.infer<typeof CareSetting>;

export const CONSENT_PATHS = ['principal_capacity', 'attorney_declaration', 'later'] as const;
export const ConsentPath = z.enum(CONSENT_PATHS);
export type ConsentPath = z.infer<typeof ConsentPath>;

export const CONSENT_TYPES = ['principal_consent', 'attorney_declaration'] as const;
export const ConsentType = z.enum(CONSENT_TYPES);
export type ConsentType = z.infer<typeof ConsentType>;

export const LEGAL_INSTRUMENT_TYPES = ['lpa_pfa', 'lpa_hw', 'epa', 'deputyship'] as const;
export const LegalInstrumentType = z.enum(LEGAL_INSTRUMENT_TYPES);
export type LegalInstrumentType = z.infer<typeof LegalInstrumentType>;

export const ATTORNEY_ACTING = ['jointly', 'jointly_and_severally', 'sole'] as const;
export const AttorneyActing = z.enum(ATTORNEY_ACTING);
export type AttorneyActing = z.infer<typeof AttorneyActing>;

export const AUDIT_ACTIONS = [
  'create',
  'update',
  'delete',
  'read',
  'export',
  'role_change',
  'invite',
  'login',
  'consent',
  'restore',
  'archive_correction',
] as const;
export const AuditAction = z.enum(AUDIT_ACTIONS);
export type AuditAction = z.infer<typeof AuditAction>;

export const DEADLINE_STATUSES = ['open', 'snoozed', 'done', 'dismissed'] as const;
export const DeadlineStatus = z.enum(DEADLINE_STATUSES);
export type DeadlineStatus = z.infer<typeof DeadlineStatus>;

export const EXPORT_STATUSES = ['pending', 'rendering', 'ready', 'failed'] as const;
export const ExportStatus = z.enum(EXPORT_STATUSES);
export type ExportStatus = z.infer<typeof ExportStatus>;

/** Analytics event names (AN-1). Props are allow-listed server side; never PII, health text or amounts. */
export const ANALYTICS_EVENTS = [
  'workspace_created',
  'principal_added',
  'stage_set',
  'first_record',
  'record_created',
  'export_generated',
  'alert_fired',
  'alert_acted',
  'trial_started',
  'subscription_started',
  'subscription_renewed',
  'subscription_cancelled',
  'screen_viewed',
  'feature_used',
  'signed_in',
  'invite_sent',
  'invite_accepted',
] as const;
export const AnalyticsEvent = z.enum(ANALYTICS_EVENTS);
export type AnalyticsEvent = z.infer<typeof AnalyticsEvent>;

/** Comment-able / linkable entity types (ARCHITECTURE §7). */
export const ENTITY_TYPES = [
  'gift',
  'transaction',
  'health_entry',
  'document',
  'decision',
  'care_event',
  'care_invoice',
  'account',
  'care_contract',
  'tax_year_summary',
  'chc_process',
  'benefit_claim',
  'principal',
  'legal_instrument',
  'consent_record',
  'attorney_expense',
  'recipient',
] as const;
export const EntityType = z.enum(ENTITY_TYPES);
export type EntityType = z.infer<typeof EntityType>;
